<template>
  <div
    class="reference-numbers-edit flex flex-column flex-gap-1 align-items-start"
  >
    <p v-if="!internalReferenceNumbers.length">
      {{
        t('common.noParam', { param: t('referenceData.referenceNumbers', 2) })
      }}
    </p>
    <ReferenceNumberEdit
      v-for="(refNum, i) of internalReferenceNumbers"
      :key="i"
      :model-value="refNum"
      :disabled="disabled"
      :types="typeOptions"
      :client-types="availableClientReferenceNumberTypes.data"
      class="align-self-stretch"
      @remove="onRemoveReferenceNumber(i)"
      @update:model-value="onUpdateReferenceNumber($event, i)"
    />
    <InputError v-for="error of errors" :key="error.$uid" :error="error" />
    <AddButton
      v-if="!hideAddButton"
      :disabled="disabled"
      :label="t('referenceData.referenceNumbers')"
      class="w-auto"
      @click="onAdd"
    />
  </div>
</template>

<script lang="ts" setup>
import { ErrorObject } from '@vuelidate/core';
import type { PropType, Ref } from 'vue';
import { computed, onMounted, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useValidations } from '@rygen/rygen-ui/composables';
import {
  Optional,
  ReferenceNumberType,
  ReferenceNumberTypeLevel,
} from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import { useReferenceNumberTypes } from '@/composables';
import { ReferenceNumber } from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';

import ReferenceNumberEdit from '@/components/referenceNumber/ReferenceNumberEdit.vue';
import AddButton from '@/components/ui/AddButton.vue';
import InputError from '@rygen/rygen-ui/components/form/InputError.vue';

const props = defineProps({
  modelValue: {
    type: Array as PropType<ReferenceNumber[]>,
    default: () => [],
  },
  errors: {
    type: Array as PropType<ErrorObject[]>,
    default: () => [],
  },
  levels: {
    type: Array as PropType<ReferenceNumberTypeLevel[]>,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  hideAddButton: {
    type: Boolean,
    default: () => false,
  },
});
const { modelValue, errors, levels, disabled, hideAddButton } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: ReferenceNumber[]): void;
}>();

const { t } = useI18n();
const { owningClient } = useParentEntityStore();
const {
  availableClientReferenceNumberTypes,
  availableReferenceNumberTypes,
  loadReferenceNumberTypes,
} = useReferenceNumberTypes(levels);

const internalReferenceNumbers: Ref<ReferenceNumber[]> = ref(modelValue.value);
useValidations(internalReferenceNumbers, {});

// Filter out already used types unless they are configured with allowMultiple
const typeOptions: Ref<ReferenceNumberType[]> = computed(() => {
  return (
    availableClientReferenceNumberTypes.value.data
      ?.filter((clientType) => {
        const existingRefNum = internalReferenceNumbers.value.find(
          (r) =>
            r.type?.naturalId &&
            r.type.naturalId === clientType.referenceNumberType?.naturalId,
        );
        return !existingRefNum || clientType.allowMultiple;
      })
      ?.map((clientType) => clientType.referenceNumberType)
      ?.filter(notNull) ?? []
  );
});

const onAdd = () => {
  internalReferenceNumbers.value.push(new ReferenceNumber());
};

const onRemoveReferenceNumber = (i: number) => {
  internalReferenceNumbers.value.splice(i, 1);
};

const onUpdateReferenceNumber = (refNum: ReferenceNumber, index: number) => {
  refNum.typeNaturalId = availableReferenceNumberTypes.value.data?.find(
    (type) => type.name === refNum?.typeName,
  )?.naturalId;
  internalReferenceNumbers.value.splice(index, 1, refNum);
};

const loadAllReferenceNumberTypes = (owningClientId: Optional<number>) => {
  if (disabled.value && !owningClientId) {
    return;
  }

  levels.value.forEach((level) => loadReferenceNumberTypes({ level }));
};

watch(
  modelValue,
  (newVal: ReferenceNumber[]) => {
    internalReferenceNumbers.value = newVal;
  },
  { immediate: true, deep: true },
);

watch(
  internalReferenceNumbers,
  (newVal: ReferenceNumber[]) => {
    emit('update:model-value', newVal);
  },
  { deep: true },
);

watch(
  () => owningClient.value?.id,
  (newVal: number | undefined) => {
    loadAllReferenceNumberTypes(newVal);
  },
  { immediate: true },
);

onMounted(() => {
  loadAllReferenceNumberTypes(owningClient.value?.id);
});
</script>
